import {
  Children,
  cloneElement,
  forwardRef,
  isValidElement,
  type ButtonHTMLAttributes,
  type MouseEventHandler,
  type ReactElement,
} from 'react'
import { cn } from '@/lib/utils'

export type ButtonVariant = 'primary' | 'navy' | 'neutral' | 'outline' | 'ghost'
export type ButtonSize = 'S' | 'M' | 'L' | 'XL'

interface ChildProps {
  className?: string
  onClick?: MouseEventHandler<HTMLElement>
  tabIndex?: number
  'aria-disabled'?: boolean
}

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant
  size?: ButtonSize
  asChild?: boolean
}

const variantClasses: Record<ButtonVariant, string> = {
  primary: cn(
    'border border-transparent bg-lc-key-500 text-lc-text-on-key hover:bg-lc-key-600',
    'disabled:bg-lc-v3-gray-400 disabled:text-lc-text-inverse',
    'aria-disabled:bg-lc-v3-gray-400 aria-disabled:text-lc-text-inverse',
  ),
  navy: cn(
    'border border-transparent bg-lc-key-900 text-lc-text-on-dark hover:bg-lc-key-950',
    'disabled:bg-lc-v3-gray-400 disabled:text-lc-text-inverse',
    'aria-disabled:bg-lc-v3-gray-400 aria-disabled:text-lc-text-inverse',
  ),
  neutral: cn(
    'border border-transparent bg-lc-v3-gray-200 text-lc-text-secondary',
    'hover:bg-lc-v3-gray-200',
    'disabled:bg-lc-v3-gray-400 disabled:text-lc-text-inverse',
    'aria-disabled:bg-lc-v3-gray-400 aria-disabled:text-lc-text-inverse',
  ),
  outline: cn(
    'border border-lc-key-500 bg-transparent text-lc-key-500',
    'hover:border-lc-key-600 hover:bg-lc-key-50 hover:text-lc-key-600',
    'disabled:border-lc-v3-gray-400 disabled:bg-transparent disabled:text-lc-text-tertiary',
    'aria-disabled:border-lc-v3-gray-400 aria-disabled:bg-transparent aria-disabled:text-lc-text-tertiary',
  ),
  ghost: cn(
    'border border-transparent bg-transparent text-lc-key-500',
    'hover:bg-lc-key-50 hover:text-lc-key-600',
    'disabled:bg-transparent disabled:text-lc-v3-gray-500',
    'aria-disabled:bg-transparent aria-disabled:text-lc-v3-gray-500',
  ),
}

const sizeClasses: Record<ButtonSize, string> = {
  S: 'h-8 rounded-lg px-3 py-1.5 text-lc-v3-label-sm font-medium',
  M: 'h-10 min-w-20 rounded-[10px] px-4 py-2.5 text-lc-v3-label-md font-semibold',
  L: 'h-12 rounded-xl px-5 py-3 text-lc-v3-label-lg font-semibold',
  XL: 'h-14 min-w-30 rounded-xl px-5 py-3 text-lc-v3-h5 font-semibold',
}

export function buttonClassName({
  variant = 'primary',
  size = 'M',
  disabled = false,
  className,
}: Pick<ButtonProps, 'variant' | 'size' | 'disabled' | 'className'> = {}): string {
  return cn(
    'inline-flex shrink-0 items-center justify-center whitespace-nowrap text-center',
    'transition-colors',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lc-key-500',
    'disabled:cursor-not-allowed disabled:pointer-events-none',
    disabled && 'cursor-not-allowed',
    variantClasses[variant],
    sizeClasses[size],
    className,
  )
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      variant = 'primary',
      size = 'M',
      asChild = false,
      disabled = false,
      className,
      type = 'button',
      children,
      onClick,
      ...rest
    },
    ref,
  ) {
    const classes = buttonClassName({ variant, size, disabled, className })

    if (asChild) {
      const child = Children.only(children)
      if (!isValidElement<ChildProps>(child)) {
        throw new Error('Button with asChild requires one valid React element')
      }

      const handleClick: MouseEventHandler<HTMLElement> = (event) => {
        if (disabled) {
          event.preventDefault()
          event.stopPropagation()
          return
        }
        child.props.onClick?.(event)
      }

      return cloneElement(child as ReactElement<ChildProps>, {
        ...rest,
        className: cn(classes, child.props.className),
        'aria-disabled': disabled || undefined,
        tabIndex: disabled ? -1 : child.props.tabIndex,
        onClick: handleClick,
      })
    }

    return (
      <button
        ref={ref}
        type={type}
        disabled={disabled}
        className={classes}
        onClick={onClick}
        {...rest}
      >
        {children}
      </button>
    )
  },
)
